import { groupAccountMapEntityName } from '../../../../backend/features/groups/shared';
import { mapDynamicFiltersReverse } from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { formatISO } from 'date-fns';
/**
 * Converts from backend storage client filter to frontend account filter. See `useAccountFilter`.
 */
export function mapAccountFilterReverse(filters, dynamicColumnData) {
    var _a;
    let result = {};
    if (filters.length > 0 && ((_a = filters[0].metadata) === null || _a === void 0 ? void 0 : _a.ui)) {
        return filters[0].metadata.ui;
    }
    result.search = findFilterValue(filters, 'search', 'full-text-query', 'string');
    result.systemRole = findFilterValue(filters, 'systemRole', 'in', 'array');
    result.firstName = findFilterValue(filters, 'firstName', '=', 'string');
    result.lastName = findFilterValue(filters, 'lastName', '=', 'string');
    result.email = findFilterValue(filters, 'email', '=', 'string');
    result.phoneNumber = findFilterValue(filters, 'contactPhoneNumbers', 'overlap', 'array-of-one');
    result.pagerNumber = findFilterValue(filters, 'pagerNumbers', 'overlap', 'array-of-one');
    const orgRole = findFilterValue(filters, 'orgRole', 'overlap', 'array');
    const orgStatus = findFilterValue(filters, 'orgStatus', 'overlap', 'array');
    if (orgRole || orgStatus) {
        const orgAssociation = new Set(result.orgAssociation);
        const orgRoleSet = new Set();
        const orgStatusSet = new Set();
        if (orgRole) {
            for (const value of orgRole) {
                const [organizationId, orgRole] = splitFilterValue(value);
                orgAssociation.add(organizationId);
                orgRoleSet.add(orgRole);
            }
        }
        if (orgStatus) {
            for (const value of orgStatus) {
                const [organizationId, orgStatus] = splitFilterValue(value);
                orgAssociation.add(organizationId);
                orgStatusSet.add(orgStatus);
            }
        }
        result.orgAssociation = orgAssociation.size > 0 ? Array.from(orgAssociation) : undefined;
        result.orgRoleSet = orgRoleSet.size > 0 ? Array.from(orgRoleSet) : undefined;
        result.orgStatusSet = orgStatusSet.size > 0 ? Array.from(orgStatusSet) : undefined;
    }
    else {
        result.orgAssociation = findFilterValue(filters, 'orgAssociation', 'overlap', 'array');
        if (!result.orgAssociation) {
            result.orgRoleSet = findFilterValue(filters, 'orgRoleSet', 'overlap', 'array');
            result.orgStatusSet = findFilterValue(filters, 'orgStatusSet', 'overlap', 'array');
        }
    }
    result.contactIssues = [];
    if (findFilterValue(filters, 'hasBouncedEmails', '=', 'boolean')) {
        result.contactIssues.push('hasBouncedEmails');
    }
    if (findFilterValue(filters, 'hasUnconfirmedPhoneNumbers', '=', 'boolean')) {
        result.contactIssues.push('hasUnconfirmedPhoneNumbers');
    }
    result.contactMethodSet = findFilterValue(filters, 'contactMethodSet', 'overlap', 'array');
    if (findFilterValue(filters, 'hasOrgMembership', '=', 'boolean') === false) {
        const systemRole = findFilterValue(filters, 'systemRole', 'not-in', 'array');
        if (systemRole && systemRole.length === 2 && systemRole.includes('system-admin') && systemRole.includes('client-admin')) {
            result.unaffiliatedOnly = true;
        }
    }
    result.systemStatus = findFilterValue(filters, 'systemStatus', 'in', 'array');
    const loginBefore = findFilterValue(filters, 'lastLogin', '<', 'number');
    if (loginBefore != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.loginBefore = formatISO(new Date(loginBefore)).substring(0, 10);
    }
    const loginAfter = findFilterValue(filters, 'lastLogin', '>', 'number');
    if (loginAfter != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.loginAfter = formatISO(new Date(loginAfter)).substring(0, 10);
    }
    const registeredBefore = findFilterValueCreated(filters, '<');
    if (registeredBefore != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.registeredBefore = formatISO(new Date(registeredBefore)).substring(0, 10);
    }
    const registeredAfter = findFilterValueCreated(filters, '>');
    if (registeredAfter != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.registeredAfter = formatISO(new Date(registeredAfter)).substring(0, 10);
    }
    const updatedBefore = findFilterValueUpdated(filters, '<');
    if (updatedBefore != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.updatedBefore = formatISO(new Date(updatedBefore)).substring(0, 10);
    }
    const updatedAfter = findFilterValueUpdated(filters, '>');
    if (updatedAfter != null) {
        // TODO this will need to change after Platform implements and we start using filtering by date string
        result.updatedAfter = formatISO(new Date(updatedAfter)).substring(0, 10);
    }
    const groupMembership = filters
        .filter((f) => !!f.groupId || (f.op === 'relation' && f.relationEntity === groupAccountMapEntityName && f.filter))
        .map((f) => { var _a, _b; return (f.groupId ? f.groupId : (_b = (_a = f.filter) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.value); });
    if (groupMembership) {
        result.groupMembership = groupMembership;
    }
    if (dynamicColumnData) {
        const dynamicFilterData = mapDynamicFiltersReverse(filters, dynamicColumnData);
        result = Object.assign(Object.assign({}, result), dynamicFilterData);
    }
    result = Object.assign(Object.assign({}, result), mapAccountFilterReverseOldFilters(filters));
    return Object.fromEntries(Object.entries(result).filter(([, value]) => value != null && (!Array.isArray(value) || value.length > 0)));
}
export function findFilterValue(filters, column, op, typeofValue) {
    for (const f of filters) {
        if ('column' in f &&
            f.column === column &&
            f.op === op &&
            'value' in f &&
            (typeofValue === 'array' || typeofValue === 'array-of-one' ? Array.isArray(f.value) : typeof f.value === typeofValue) &&
            f.groupId == null) {
            if (typeofValue === 'array-of-one') {
                return f.value.length === 1 ? f.value[0] : undefined;
            }
            else {
                return f.value;
            }
        }
    }
    return undefined;
}
function findFilterValueCreated(filters, op) {
    for (const f of filters) {
        if ('created' in f && f.op === op && f.groupId == null) {
            return f.created;
        }
    }
    return undefined;
}
function findFilterValueUpdated(filters, op) {
    for (const f of filters) {
        if ('updated' in f && f.op === op && f.groupId == null) {
            return f.updated;
        }
    }
    return undefined;
}
export function splitFilterValue(value) {
    const index = value.indexOf('_');
    if (index !== -1) {
        return [value.substring(0, index), value.substring(index + 1)];
    }
    else {
        return [value, ''];
    }
}
function mapAccountFilterReverseOldFilters(filters) {
    var _a;
    const result = {};
    {
        // handle old filter by voice/email/pager
        const orFilters = (_a = filters.find((f) => f.op === 'or' && 'filter' in f && Array.isArray(f.filter))) === null || _a === void 0 ? void 0 : _a.filter;
        if (orFilters) {
            const contactPhoneNumberTypeSet = findFilterValue(orFilters, 'contactPhoneNumberTypeSet', 'overlap', 'array');
            const contactEmails = findFilterValue(orFilters, 'contactEmails', '!=', 'array');
            const pagerNumbers = findFilterValue(orFilters, 'pagerNumbers', '!=', 'array');
            if (contactPhoneNumberTypeSet || contactEmails || pagerNumbers) {
                result.contactMethodSet = [];
                if (contactPhoneNumberTypeSet) {
                    result.contactMethodSet.push(...contactPhoneNumberTypeSet);
                }
                if (contactEmails && contactEmails.length === 0) {
                    result.contactMethodSet.push('email');
                }
                if (pagerNumbers && pagerNumbers.length === 0) {
                    result.contactMethodSet.push('pager');
                }
            }
        }
    }
    {
        // handle some other case here
    }
    return result;
}
